import { JSX } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { Job } from 'types/types';
import { JobInfo } from '../ui/JobInfo';
import { Scrollable } from '../Scrollable';
import { StyledMarkdown } from '../StyledMarkdown';
import { JobMatchScore } from './JobMatchScore';
import { JobRequirementAccordion } from './JobRequirementAccordion';
import { SkillMatch } from './jobMatchTypes';

interface JobMatchAnalysisViewProps {
  job: Job;
  variant: 'small' | 'normal';
  isMobile: boolean;
  analyzing: boolean;
  overallScore: number;
  percentage: number;
  startAnalysis: boolean;
  loadingRequirements: boolean;
  skillMatches: SkillMatch[];
  expanded: string | false;
  matchStatus: string;
  reasoningIndex: number | null;
  dividerColor: string;
  getMatchColor: (score: number) => string;
  getStatusIcon: (status: string, score: number) => JSX.Element;
  handleAccordionChange: (
    panel: string
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  handleAssessmentRegenerate: (index: number, skill: string, force: boolean) => Promise<void>;
  beginAnalysis: () => void;
  setReasoningIndex: (index: number | null) => void;
}

function JobMatchAnalysisView(props: JobMatchAnalysisViewProps): JSX.Element {
  return (
    <>
      <Scrollable
        className="JobMatchAnalysis"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          m: 0,
          p: 1,
          width: '100%',
          minHeight: 0,
          flexGrow: 1,
        }}
      >
        {props.variant !== 'small' && <JobInfo job={props.job} variant="normal" />}
        <AnalysisHeader {...props} />
        {props.loadingRequirements ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Analyzing job requirements...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Requirements Analysis
            </Typography>
            {props.skillMatches.map((match, index) => (
              <JobRequirementAccordion
                key={index}
                match={match}
                index={index}
                expanded={props.expanded}
                matchStatus={props.matchStatus}
                dividerColor={props.dividerColor}
                getMatchColor={props.getMatchColor}
                getStatusIcon={props.getStatusIcon}
                onAccordionChange={props.handleAccordionChange}
                onRegenerate={props.handleAssessmentRegenerate}
                onShowReasoning={props.setReasoningIndex}
              />
            ))}
          </Box>
        )}
      </Scrollable>
      <Dialog
        open={props.reasoningIndex !== null}
        onClose={(): void => props.setReasoningIndex(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Reasoning
          {props.reasoningIndex !== null && `: ${props.skillMatches[props.reasoningIndex]?.skill}`}
        </DialogTitle>
        <DialogContent dividers>
          {props.reasoningIndex !== null && (
            <StyledMarkdown content={props.skillMatches[props.reasoningIndex]?.reasoning ?? ''} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={(): void => props.setReasoningIndex(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function AnalysisHeader(props: JobMatchAnalysisViewProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        mb: props.isMobile ? 1 : 2,
        gap: 1,
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: props.isMobile ? 'column' : 'row',
          flexGrow: 1,
          gap: 1,
        }}
      >
        {props.analyzing && props.overallScore !== 0 && (
          <JobMatchScore
            score={props.overallScore}
            sx={{ width: props.isMobile ? '100%' : 'auto', flexGrow: 1 }}
          />
        )}
        {props.analyzing && (
          <Paper
            sx={{
              width: '10rem',
              ml: 1,
              p: 1,
              gap: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h5" component="h2" sx={{ m: 0, fontSize: '1rem' }}>
              Analyzing
            </Typography>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={props.percentage}
                size={60}
                thickness={5}
                sx={{ color: 'orange' }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ fontWeight: 'bold' }}
                >{`${Math.round(props.percentage)}%`}</Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
      <Button
        sx={{ marginLeft: 'auto' }}
        disabled={props.analyzing || props.startAnalysis}
        onClick={props.beginAnalysis}
        variant="contained"
      >
        {props.analyzing ? 'Assessment in Progress' : 'Analyze Waiting Skills'}
      </Button>
    </Box>
  );
}

export { JobMatchAnalysisView };
